'use client';

import { Button } from '@components/button';
import { CaseSliderCard } from '@components/casesSlider';
import { PageSection } from '@components/pageSection';
import { classNames } from '@lib/classNames/classNames';
import { memo, useCallback, useEffect, useState } from 'react';

import { CasesContent } from './ui/CasesContent';
import { Industry } from './ui/Industry';
import { Product } from './ui/Product';

import getCases from '@/shared/api/getCases';
import getImageUrl from '@/shared/api/getImageUrl';
import { industryLabels, Locale, productLabels } from '@/shared/types';
import {
  filterCasesByLocale,
  formatCasesItems,
  translateCasesItems,
} from '@/shared/utils/cases';

interface CasesProps {
  translations: Record<string, string>;
  locale: Locale;
}

export const Cases = memo(({ translations, locale }: CasesProps) => {
  const [productsFilter, setProductsFilter] = useState<string[]>([]);
  const [industriesFilter, setIndustriesFilter] = useState<string[]>([]);
  const [cases, setCases] = useState<CaseSliderCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const isClearDisabled =
    productsFilter.length === 0 && industriesFilter.length === 0;

  const updateCases = useCallback(
    async ({
      products,
      industries,
    }: {
      products: string[];
      industries: string[];
    }) => {
      try {
        setIsLoading(true);

        const casesResponse = await getCases({
          products,
          industries,
          limit: '40',
          offset: '0',
        });

        const localizedCases = filterCasesByLocale(casesResponse.items, locale);
        const translatedCases = translateCasesItems(
          localizedCases,
          translations,
        );
        const formattedCases = formatCasesItems(translatedCases);

        const casesWithImages = await Promise.all(
          formattedCases.map(async (el) => {
            const { imageUrl } = await getImageUrl(el.img);
            return { ...el, img: imageUrl };
          }),
        );

        setCases(casesWithImages);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load cases:', error);

        setCases([]);
        setIsLoading(false);
      }
    },
    [translations, locale],
  );

  const handleClear = useCallback(() => {
    setIndustriesFilter([]);
    setProductsFilter([]);

    window.history.replaceState(null, '', '/cases');

    updateCases({ products: [], industries: [] });
  }, [updateCases]);

  const handleIndustryChange = useCallback(
    (newIndustries: string[], url: string) => {
      setIndustriesFilter(newIndustries);

      window.history.replaceState(null, '', url);

      updateCases({
        products: productsFilter,
        industries: newIndustries,
      });
    },
    [productsFilter, updateCases],
  );

  const handleProductChange = useCallback(
    (newProducts: string[], url: string) => {
      setProductsFilter(newProducts);

      window.history.replaceState(null, '', url);

      updateCases({
        products: newProducts,
        industries: industriesFilter,
      });
    },
    [industriesFilter, updateCases],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const products = params.getAll('products');
    const industries = params.getAll('industries');

    setProductsFilter(products);
    setIndustriesFilter(industries);

    updateCases({
      products,
      industries,
    });
  }, [updateCases]);

  const isButtonClearDisabled = isLoading || isClearDisabled;

  return (
    <PageSection childrenWrapperClassName="px-4">
      <section className="mb-12 flex flex-wrap gap-x-8 gap-y-6 max-sm:mb-16">
        <section className="basis-[calc(50%-1rem)] max-sm:basis-full">
          <p className="mb-6 text-lg leading-6 font-medium max-sm:mb-4">
            {translations.casesSectionCategoriesProductsTitle ?? 'Продукт 2ГИС'}
          </p>
          <div className="flex flex-wrap gap-3">
            {Object.values(productLabels).map((product) => (
              <Product
                key={product}
                product={product}
                industriesFilter={industriesFilter}
                productsFilter={productsFilter}
                onProductChange={handleProductChange}
              />
            ))}
          </div>
        </section>
        <section className="basis-[calc(50%-1rem)] flex-wrap max-sm:basis-full">
          <p className="mb-6 text-lg leading-6 font-medium max-sm:mb-4">
            {translations.casesSectionCategoriesIndustriesTitle ?? 'Отрасль'}
          </p>
          <div className="flex flex-wrap gap-3">
            {Object.values(industryLabels).map((industry) => (
              <Industry
                key={industry}
                industriesFilter={industriesFilter}
                productsFilter={productsFilter}
                industry={industry}
                onIndustryChange={handleIndustryChange}
              />
            ))}
          </div>
        </section>
        <Button
          type="button"
          onClick={handleClear}
          className={classNames({
            cls: 'cursor-pointer text-[#60C949] opacity-50',
            mods: {
              ['text-text-base-secondary']: isButtonClearDisabled,
            },
          })}
          disabled={isButtonClearDisabled}
        >
          Сбросить выбранное
        </Button>
      </section>
      <section className="relative mb-36 grid min-h-[219px] grid-cols-2 gap-8 max-sm:mb-16 max-sm:flex max-sm:flex-col max-sm:gap-6">
        <CasesContent cases={cases} isLoading={isLoading} />
      </section>
    </PageSection>
  );
});

Cases.displayName = 'CasesPage';
